import type { EntrySource } from "./entry-source";
import type {
  CustomTextMessage,
  GamePlayer,
  PlayerDataUpdate,
  PlayerProfile,
  PublicPlayer,
  PurchaseData,
  RichMessage,
  SubscriptionData,
} from "./types";

// Common message properties
type CommonProps = {
  conversationId?: string;
};

export type NameValidationError = "too_short" | "too_long" | "inappropriate";

// Only export message types that games need to know about
export type BeginPlatformLoginMessage = CommonProps & {
  type: "BeginPlatformLogin";
  entryPayload?: string;
};

export type BeginPlatformRegistrationOverlayMessage = CommonProps & {
  conversationId: string;
  type: "BeginPlatformRegistrationOverlay";
  theme?: "light" | "dark";
  entryPayload?: string;
  /** Game-authored template; the host trims it to a single text before leasing. */
  registrationMessage?: string | null;
};

export type PlatformRegistrationOverlayLoginMessage = CommonProps & {
  conversationId: string;
  type: "PlatformRegistrationOverlayLogin";
};

export type PlatformRegistrationOverlayClosedMessage = CommonProps & {
  type: "PlatformRegistrationOverlayClosed";
};

export type PlatformLoginClosedMessage = CommonProps & {
  type: "PlatformLoginClosed";
};

export type DismissPlatformRegistrationOverlayMessage = CommonProps & {
  type: "DismissPlatformRegistrationOverlay";
};

export type BeginPlatformPurchaseMessage = CommonProps & {
  type: "BeginPlatformPurchase";
  productSku: string;
};

export type BeginPlatformPurchaseErrorCode =
  | "internal_error"
  | "invalid_product";

export type BeginPlatformPurchaseResponseMessage = CommonProps &
  ({
    type: "BeginPlatformPurchaseResponse";
  } & (
    | {
        result: "success";
        purchase: PurchaseData;
        purchaseSigned: string;
      }
    | {
        result: "cancel";
      }
    | {
        result: "error";
        error: BeginPlatformPurchaseErrorCode;
      }
  ));

export type CompletePlatformPurchaseMessage = CommonProps & {
  type: "CompletePlatformPurchase";
  purchaseToken: string;
};

export type CompletePlatformPurchaseErrorCode =
  | "internal_error"
  | "invalid_token";

export type CompletePlatformPurchaseResponseMessage = CommonProps & {
  type: "CompletePlatformPurchaseResponse";
} & (
    | { result: "success" }
    | {
        result: "error";
        error: CompletePlatformPurchaseErrorCode;
      }
  );

export type BeginPlatformSubscriptionMessage = CommonProps & {
  type: "BeginPlatformSubscription";
  subscriptionSku: string;
};

export type BeginPlatformSubscriptionErrorCode =
  | "internal_error"
  | "invalid_subscription"
  | "already_subscribed"
  | "guest_not_allowed";

export type BeginPlatformSubscriptionResponseMessage = CommonProps &
  ({
    type: "BeginPlatformSubscriptionResponse";
  } & (
    | {
        result: "success";
        subscription: SubscriptionData;
        subscriptionSigned: string;
      }
    | {
        result: "cancel";
      }
    | {
        result: "error";
        error: BeginPlatformSubscriptionErrorCode;
      }
  ));

export type AcquireLoginLeaseMessage = CommonProps & {
  type: "AcquireLoginLease";
  message: string;
  replyMessage: CustomTextMessage | null;
  reminderMessage: CustomTextMessage | null;
  replyMessageRaw: RichMessage | null;
  reminderMessageRaw: RichMessage | null;
  keywords: string[] | null;
  entryPayload?: string;
  targetGameSlug?: string | null;
};

export type ValidateNameMessage = CommonProps & {
  type: "ValidateName";
  name: string;
};

export type RedirectToGameMessage = CommonProps & {
  type: "RedirectToGame";
  entryPayload?: string;
  skipGameExitConfirm?: boolean;
} & ({ redirectToFlagship: true } | { gameSlug: string });

export type RedirectToTeamGameMessage = CommonProps & {
  type: "RedirectToTeamGame";
  gameSlug: string;
  entryPayload?: string;
  skipGameExitConfirm?: boolean;
};

export type RedirectToTeamGameErrorCode =
  | "game_not_found"
  | "different_team"
  | "internal_error";

export type RedirectToTeamGameResponseMessage = CommonProps & {
  type: "RedirectToTeamGameResponse";
} & (
    | { success: true }
    | { success: false; error: RedirectToTeamGameErrorCode }
  );

export type RedirectToExplorePageMessage = CommonProps & {
  type: "RedirectToExplorePage";
};

export type SendReservedLoginMessageMessage = CommonProps & {
  type: "SendReservedLoginMessage";
  link: string;
};

export type ReserveBrowserContinuationMessage = CommonProps & {
  type: "ReserveBrowserContinuation";
  entryPayload?: string;
  path?: string;
};

export type BrowserContinuationReservedMessage = CommonProps &
  (
    | {
        type: "BrowserContinuationReserved";
        success: true;
        token: string;
      }
    | {
        type: "BrowserContinuationReserved";
        success: false;
        error: "no_target_game" | "invalid_path" | "not_entitled";
      }
  );

export type OpenReservedBrowserContinuationMessage = CommonProps & {
  type: "OpenReservedBrowserContinuation";
  token: string;
};

export type OpenLegalPageMessage = CommonProps & {
  type: "OpenLegalPage";
  page: "privacy" | "terms" | "copyright";
};

export type InitializedMessage = CommonProps & {
  type: "Initialized";
  sdkContextId: string;
  windowLocationHref: string;
  autoLoginReminders?: boolean;
};

/**
 * Diagnostic message sent when the iframe is restored from browser BFCache.
 * Helps diagnose sdk_context_mismatch issues.
 */
export type IframeBfcacheRestoreMessage = CommonProps & {
  type: "IframeBfcacheRestore";
  sdkContextId: string;
  windowLocationHref: string;
};

export type SetPlayerMessage = CommonProps & {
  type: "SetPlayer";
  player: GamePlayer;
  profile: PlayerProfile | null;
  entrySource?: EntrySource;
};

export type NotificationPriority = "low" | "medium" | "high" | "critical";

export type ScheduleNotificationV2Message = CommonProps & {
  type: "ScheduleNotificationV2";
  entryPayload?: Record<string, unknown>;
  identifier?: string;
  /** @deprecated Use assetReference instead */
  imageReference?: string;
  assetReference?: string;
  asset?: string;
  ctaText: string;
  body: string;
  title?: string;
  priority: NotificationPriority;
} & (
    | { scheduledAt: string; scheduledInDays?: never } // ISO string for exact scheduling
    | { scheduledAt?: never; scheduledInDays: number } // Days offset for fuzzy scheduling
  );

export type UnscheduleNotificationMessage = CommonProps & {
  type: "UnscheduleNotification";
  identifier: string;
};

export type ScheduleNotificationMessage = ScheduleNotificationV2Message;

export type InteractiveNotificationOption = {
  key: string;
  label: string;
};

export type InteractiveNotificationStepMessage = {
  key: string;
  body: string;
  options: InteractiveNotificationOption[];
};

export type InteractiveNotificationTerminalMessage = {
  key: string;
  body: string;
  ctaText: string;
};

export type InteractiveNotificationMessage =
  | InteractiveNotificationStepMessage
  | InteractiveNotificationTerminalMessage;

export type ScheduleInteractiveNotificationMessage = CommonProps & {
  type: "ScheduleInteractiveNotification";
  messages: InteractiveNotificationMessage[];
  entryPayload?: Record<string, unknown>;
  identifier?: string;
  assetReference?: string;
  priority: NotificationPriority;
} & (
    | { scheduledAt: string; scheduledInDays?: never }
    | { scheduledAt?: never; scheduledInDays: number }
  );

export type ValidateNameResponseMessage = CommonProps & {
  type: "ValidateNameResponse";
} & (
    | {
        success: true;
        status: "invalid";
        validationError: NameValidationError;
      }
    | {
        success: true;
        status: "valid";
      }
    | {
        success: false;
        error: "internal_error" | "not_entitled";
      }
  );

export type CaptureEventOptions = {
  send_instantly?: boolean;
  transport?: "XHR" | "fetch" | "sendBeacon";
};

export type CaptureEventMessage = CommonProps & {
  type: "CaptureEvent";
  options?: CaptureEventOptions;
  event: string;
  properties: Record<string, unknown>;
};

export type SdkMethodCalledMessage = CommonProps & {
  type: "SdkMethodCalled";
  method: string;
  args: string;
  clientSdkContextId: string;
  clientIframeUrl: string;
  updateId?: number;
  stateIncrement?: number | null;
};

export type SdkMethodErrorMessage = CommonProps & {
  type: "SdkMethodError";
  method: string;
  args: string;
  error: string;
};

export const validOnboardingEvents = [
  "sms_modal_show",
  "exit_popup_show",
  "continue_exit_click",
  "abandon_exit_click",
  "game_instance_create",
  "game_load_start",
  "game_load_complete",
  "game_scene_initialize",
  "game_scene_enter",
  "unique_text_start",
  "unique_text_submit",
  "onboarding_complete",
  "onboarding_fail",
] as const;
export type OnboardingAnalyticsEvent = (typeof validOnboardingEvents)[number];

export type OnboardingCaptureEventMessage = CommonProps & {
  type: "OnboardingCaptureEvent";
  event:
    | "first_interaction"
    | "sms_button_tap"
    | "engagement_summary"
    | OnboardingAnalyticsEvent;
  properties?: Record<string, unknown>;
};

// Export only the message types games need to handle
export type UpdatePlayerDataMessage = CommonProps & {
  type: "UpdatePlayerData";
  id: number;
  update: PlayerDataUpdate;
};

export type AckUpdatePlayerDataMessage = CommonProps & {
  type: "AckUpdatePlayerData";
  id: number;
};

export type LoginLeaseAcquiredMessage = CommonProps &
  (
    | {
        type: "LoginLeaseAcquired";
        success: true;
        link: string;
      }
    | {
        type: "LoginLeaseAcquired";
        success: false;
        error: "internal_error" | "invalid_target_game" | "not_entitled";
        link: "";
      }
  );

export type GetGameProductsMessage = CommonProps & {
  type: "GetGameProducts";
};

export type GetGameProductsResponseMessage = CommonProps & {
  type: "GetGameProductsResponse";
  success: boolean;
  products: Array<{
    sku: string;
    name: string;
    description: string | null;
    price: number;
    currency: string;
  }>;
};

export type CancelPlatformSubscriptionMessage = CommonProps & {
  type: "CancelPlatformSubscription";
  subscriptionSku: string;
};

export type CancelPlatformSubscriptionErrorCode =
  | "internal_error"
  | "not_found"
  | "not_active"
  | "guest_not_allowed";

export type CancelPlatformSubscriptionResponseMessage = CommonProps &
  ({
    type: "CancelPlatformSubscriptionResponse";
  } & (
    | { result: "success" }
    | { result: "cancel" }
    | {
        result: "error";
        error: CancelPlatformSubscriptionErrorCode;
      }
  ));

export type ClaimRetentionOfferMessage = CommonProps & {
  type: "ClaimRetentionOffer";
  subscriptionSku: string;
};

export type ClaimRetentionOfferErrorCode =
  | "internal_error"
  | "not_eligible"
  | "guest_not_allowed";

export type ClaimRetentionOfferResponseMessage = CommonProps &
  ({
    type: "ClaimRetentionOfferResponse";
  } & (
    | {
        result: "success";
        subscription: SubscriptionData;
        subscriptionSigned: string;
      }
    | {
        result: "error";
        error: ClaimRetentionOfferErrorCode;
      }
  ));

export type GetSubscriptionsMessage = CommonProps & {
  type: "GetSubscriptions";
};

export type GetSubscriptionsResponseMessage = CommonProps &
  (
    | {
        type: "GetSubscriptionsResponse";
        success: true;
        subscriptions: SubscriptionData[];
        signed: string;
      }
    | { type: "GetSubscriptionsResponse"; success: false }
  );

export type GetIncompletePurchasesMessage = CommonProps & {
  type: "GetIncompletePurchases";
};

export type ShowRewardedAdMessage = CommonProps & {
  type: "ShowRewardedAd";
};

export type ShowRewardedAdResponseMessage = CommonProps & {
  type: "ShowRewardedAdResponse";
} & (
    | { success: true; result: "started" | "no_fill" }
    | { success: false; error: "internal_error" }
  );

export type RewardData = {
  rewardId: string;
  /** Milliseconds since the epoch. */
  earnedAt: number;
};

export type GetIncompleteRewardsMessage = CommonProps & {
  type: "GetIncompleteRewards";
};

export type GetIncompleteRewardsResponseMessage = CommonProps & {
  type: "GetIncompleteRewardsResponse";
} & (
    | { success: true; rewards: RewardData[]; rewardsSigned: string }
    | { success: false }
  );

export type CompleteRewardMessage = CommonProps & {
  type: "CompleteReward";
  rewardId: string;
};

export type CompleteRewardResponseMessage = CommonProps & {
  type: "CompleteRewardResponse";
} & (
    | { result: "success" }
    | { result: "error"; error: "internal_error" | "invalid_reward" }
  );

export type GetPlayerSignedMessage = CommonProps & {
  type: "GetPlayerSigned";
};

export type GetPlayerSignedResponseMessage = CommonProps & {
  type: "GetPlayerSignedResponse";
} & (
    | {
        success: true;
        player: PublicPlayer;
        playerSigned: string;
      }
    | { success: false }
  );

export type GetIncompletePurchasesResponseMessage = CommonProps & {
  type: "GetIncompletePurchasesResponse";
} & (
    | { success: false }
    | {
        success: true;
        hasMore: boolean;
        purchases: Array<PurchaseData>;
        purchasesSigned: string;
      }
  );

export type ReferralNotificationVariant = {
  title?: string | null;
  body: string;
  ctaText: string;
  imageReference?: string | null;
};

export type ReferralNotificationTemplate = {
  // Template applies when conversion count >= minConversionCount.
  // Server picks the template with the highest matching min.
  minConversionCount: number;
  variants: ReferralNotificationVariant[];
};

export type ShareReferralLinkMessage = CommonProps & {
  type: "ShareReferralLink";
  reference: string;
  entryPayload?: Record<string, unknown>;
  shareTitle?: string;
  shareText?: string;
  onboardingSlug?: string;
  notificationTemplates?: ReferralNotificationTemplate[];
  /**
   * Base64 data URL of an image (e.g. from `canvas.toDataURL("image/png")`)
   * to use as the og:image when this referral is opened. The platform
   * decodes, hashes, deduplicates against previously-uploaded images with
   * the same content, hosts the bytes on its CDN, and stores the resulting
   * URL on the referral.
   *
   * Accepted MIME: image/png, image/jpeg, image/webp, image/gif. The data
   * URL must be at most 2 MB. Animated GIFs are hosted unmodified, so they
   * stay animated in link previews.
   */
  shareImage?: string;
};

export type ShareReferralLinkResponseMessage = CommonProps & {
  type: "ShareReferralLinkResponse";
} & (
    | {
        success: true;
        canceled: boolean;
      }
    | {
        success: false;
        error: "internal_error" | "share_failed" | "unauthorized";
      }
  );

export type ShareImageMessage = CommonProps & {
  type: "ShareImage";
  /**
   * Base64 data URL of the image to share (e.g. from
   * `canvas.toDataURL("image/png")`). Omit it to have the platform capture the
   * game's canvas, or its registered screenshot provider, as the footer's
   * screenshot button does.
   *
   * Accepted MIME: image/png, image/jpeg, image/webp, image/gif, at most 2 MB.
   */
  image?: string;
  /**
   * Handed back to the game when a player opens the shared message, so a code
   * or coupon travels with it. The platform mints a link to this game carrying
   * the payload and appends it to the posted message.
   */
  entryPayload?: Record<string, unknown>;
};

export type ShareImageErrorCode =
  | "internal_error"
  | "share_failed"
  | "unauthorized"
  | "not_available"
  | "capture_failed";

export type ShareImageResponseMessage = CommonProps & {
  type: "ShareImageResponse";
  success: boolean;
  canceled?: boolean;
  error?: ShareImageErrorCode;
};

export type GetFeatureFlagMessage = CommonProps & {
  type: "GetFeatureFlag";
  key: string;
};

export type GetFeatureFlagResponseMessage = CommonProps & {
  type: "GetFeatureFlagResponse";
} & (
    | { success: true; value: string | undefined }
    | { success: false; error: string }
  );

export type GetReferralsMessage = CommonProps & {
  type: "GetReferrals";
};

export type SetLoadingProgressMessage = CommonProps & {
  type: "SetLoadingProgress";
  progress: number;
};

export type TakeScreenshotMessage = CommonProps & {
  type: "TakeScreenshot";
};

export type PlatformExitRequestedMessage = CommonProps & {
  type: "PlatformExitRequested";
};

export type TakeScreenshotErrorCode =
  | "not_available"
  | "capture_failed"
  | "blank";

export type TakeScreenshotResponseMessage = CommonProps & {
  type: "TakeScreenshotResponse";
} & (
    | {
        success: true;
        /** Data URL (image/png) of the game's main canvas. */
        image: string;
      }
    | {
        success: false;
        error: TakeScreenshotErrorCode;
      }
  );

export type GameLoadedMessage = CommonProps & {
  type: "GameLoaded";
};

/** Retired signal; still in the union so builds on older SDKs stay handled. */
export type FirstMilestoneMessage = CommonProps & {
  type: "FirstMilestone";
};

export type GetReferralsResponseMessage = CommonProps & {
  type: "GetReferralsResponse";
} & (
    | {
        success: true;
        referrals: {
          [reference: string]: { playerId: string; joinedAt: string }[];
        };
        referralsSigned: string;
      }
    | {
        success: false;
        error: "internal_error" | "unauthorized";
      }
  );

export type SdkMessage =
  | SetPlayerMessage
  | AckUpdatePlayerDataMessage
  | LoginLeaseAcquiredMessage
  | BrowserContinuationReservedMessage
  | PlatformRegistrationOverlayClosedMessage
  | PlatformLoginClosedMessage
  | GetGameProductsResponseMessage
  | GetSubscriptionsResponseMessage
  | BeginPlatformPurchaseResponseMessage
  | CompletePlatformPurchaseResponseMessage
  | GetIncompletePurchasesResponseMessage
  | GetPlayerSignedResponseMessage
  | BeginPlatformSubscriptionResponseMessage
  | CancelPlatformSubscriptionResponseMessage
  | ClaimRetentionOfferResponseMessage
  | ShareReferralLinkResponseMessage
  | ShareImageResponseMessage
  | GetReferralsResponseMessage
  | ValidateNameResponseMessage
  | GetFeatureFlagResponseMessage
  | RedirectToTeamGameResponseMessage
  | ShowRewardedAdResponseMessage
  | GetIncompleteRewardsResponseMessage
  | CompleteRewardResponseMessage
  | TakeScreenshotMessage
  | PlatformExitRequestedMessage;

export type HostMessage =
  | BeginPlatformLoginMessage
  | BeginPlatformRegistrationOverlayMessage
  | PlatformRegistrationOverlayLoginMessage
  | DismissPlatformRegistrationOverlayMessage
  | BeginPlatformPurchaseMessage
  | CompletePlatformPurchaseMessage
  | BeginPlatformSubscriptionMessage
  | CancelPlatformSubscriptionMessage
  | ClaimRetentionOfferMessage
  | InitializedMessage
  | IframeBfcacheRestoreMessage
  | ScheduleNotificationMessage
  | ScheduleInteractiveNotificationMessage
  | UnscheduleNotificationMessage
  | CaptureEventMessage
  | UpdatePlayerDataMessage
  | AcquireLoginLeaseMessage
  | ValidateNameMessage
  | RedirectToGameMessage
  | RedirectToTeamGameMessage
  | ShowRewardedAdMessage
  | GetIncompleteRewardsMessage
  | CompleteRewardMessage
  | RedirectToExplorePageMessage
  | SendReservedLoginMessageMessage
  | ReserveBrowserContinuationMessage
  | OpenReservedBrowserContinuationMessage
  | OpenLegalPageMessage
  | OnboardingCaptureEventMessage
  | GetGameProductsMessage
  | GetSubscriptionsMessage
  | SdkMethodCalledMessage
  | SdkMethodErrorMessage
  | GetIncompletePurchasesMessage
  | GetPlayerSignedMessage
  | ShareReferralLinkMessage
  | ShareImageMessage
  | GetReferralsMessage
  | GetFeatureFlagMessage
  | SetLoadingProgressMessage
  | GameLoadedMessage
  | FirstMilestoneMessage
  | TakeScreenshotResponseMessage;

export type Message = SdkMessage | HostMessage;

export type MessageType = Message["type"];
export type SdkMessageType = SdkMessage["type"];
export type HostMessageType = HostMessage["type"];

/**
 * The matching Message type with a given type value (T).
 * Useful for generically typing message when the type value is not yet known
 */
export type MessageByType<T extends MessageType> = Extract<
  Message,
  { type: T }
>;

export type SdkMessageByType<T extends SdkMessageType> = Extract<
  SdkMessage,
  { type: T }
>;

export type HostMessageByType<T extends HostMessageType> = Extract<
  HostMessage,
  { type: T }
>;

export function isTextClubMessage(data: unknown): data is (
  | SdkMessage
  | HostMessage
) & {
  sdkContextId?: string;
  sdkVersion?: string;
} {
  return (
    typeof data === "object" &&
    data !== null &&
    "source_channel" in data &&
    (data as { source_channel: string }).source_channel === "textclub"
  );
}

export interface ChildBridge {
  sendMessage(message: HostMessage): void;
  onMessage(message: SdkMessage): void;
}

export interface HostBridge {
  sendMessage(message: SdkMessage): void;
  onMessage(message: HostMessage, context: { sdkVersion: string }): void;
  destroy(): void;
}
